import { Theme } from '@insights-gaming/theme';
import { Button, DialogActions, TooltipProps } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { StyledTooltip } from '../StyledTooltip';

export interface MultiStepProductTourCommonProps {
  anchors: HTMLElement[];
  open: boolean;
  onClose: VoidFunction;
  placements?: TooltipProps['placement'][];
}

export interface MultiStepTitleComponentProps {
  progress?: {
    current: number;
    total: number;
  };
  goToStep: (step: number) => void;
  actions: ReactNode;
  onClose: VoidFunction;
}

interface MultiStepProductTourOwnProps {
  className?: string;
  titleComponents: React.ComponentType<MultiStepTitleComponentProps>[];
  onNext?: (current: number) => void;
  prev: string;
  next: string;
  done: string;
}

type MultiStepProductTourProps = MultiStepProductTourOwnProps & MultiStepProductTourCommonProps;

const useStyles = makeStyles<Theme>(createStyles({
  actions: {
    '&$multipleActions': {
      justifyContent: 'space-between',
    },
  },
  multipleActions: {},
}), { name: 'MultiStepProductTour' });

/**
 * Use HOC to bind redux
 */
export function MultiStepProductTour(props: MultiStepProductTourProps) {
  const classes = useStyles(props);
  const {
    anchors,
    done,
    next,
    onClose,
    open,
    placements,
    prev,
    titleComponents,
  } = props;

  if (anchors.length !== titleComponents.length) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error('MultiStepProductTour: mismatch length');
    }
    console.error('MultiStepProductTour: mismatch length');
  }

  const [step, setStep] = useState(0);
  const numSteps = anchors.length;

  const handlePrev = useMemo(() => {
    return step > 0
      ? () => setStep(p => p - 1)
      : undefined;
  }, [step]);

  const handleNext = useMemo(() => {
    return step < numSteps - 1
      ? () => setStep(p => p + 1)
      : undefined;
  }, [numSteps, step]);

  useEffect(() => {
    const currentAnchorEl = anchors[step];
    if (!currentAnchorEl) {
      return;
    }
    if (open) {
      currentAnchorEl.addEventListener('click', handleNext ? handleNext : onClose);
    }
    return () => {
      currentAnchorEl.removeEventListener('click', handleNext ? handleNext : onClose);
    };
  }, [anchors, handleNext, onClose, open, step]);

  const TitleComponent = titleComponents[step];

  return (
    <StyledTooltip
    PopperProps={{
      anchorEl: anchors[step],
    }}
    arrow={true}
    placement={placements?.[step] || 'bottom'}
    open={open}
    interactive={true}
    title={(
      TitleComponent && (
        <TitleComponent
        progress={{
          current: step,
          total: numSteps,
        }}
        goToStep={setStep}
        onClose={onClose}
        actions={(
          <DialogActions className={classNames(classes.actions, { [classes.multipleActions]: handlePrev })}>
            {handlePrev && (
              <Button size='small' startIcon={<ChevronLeftIcon />} onClick={handlePrev}>
                {prev}
              </Button>
            )}
            {handleNext ? (
              <Button size='small' variant='contained' color='primary' onClick={handleNext}>
                {next}
              </Button>
            ) : (
              <Button size='small' variant='contained' color='primary' onClick={onClose}>
                {done}
              </Button>
            )}
          </DialogActions>
        )}
        />
      )
    )}
    >
      <span />
    </StyledTooltip>
  );
}
